const DEFAULT_MAX_TEXT_CHARS = 20000;
const DEFAULT_MAX_LINKS = 100;
const DEFAULT_MAX_LINK_CHARS = 20000;
const MAX_URL_CHARS = 2048;
const PRIVATE_SELECTOR = "[data-private]";
const HIDDEN_SELECTOR = '[hidden],[aria-hidden="true"]';
const EXCLUDED_CONTENT_SELECTOR = [
  PRIVATE_SELECTOR,
  HIDDEN_SELECTOR,
  "script",
  "style",
  "noscript",
  "template",
  "svg",
  "canvas",
  "ctm-chat",
].join(",");

/**
 * Convert a URL-like value to an HTTP(S) URL.
 * @param {string} value URL or relative URL to parse.
 * @param {string} baseUrl Base URL used for relative values.
 * @returns {URL|null} Parsed safe URL, or null when invalid.
 */
export function parseHttpUrl(value, baseUrl) {
  if (
    typeof value !== "string" ||
    !value.trim() ||
    value.trim().length > MAX_URL_CHARS
  ) {
    return null;
  }

  try {
    const url = new URL(value.trim(), baseUrl);
    if (!["http:", "https:"].includes(url.protocol)) {
      return null;
    }
    if (url.username || url.password) {
      return null;
    }
    return url;
  } catch (_error) {
    return null;
  }
}

/**
 * Normalize a URL for exact policy comparisons.
 * @param {URL} url URL to normalize.
 * @returns {string} Comparable URL.
 */
function comparableUrl(url) {
  const comparable = new URL(url.toString());
  comparable.hash = "";
  if (comparable.pathname.length > 1) {
    comparable.pathname = comparable.pathname.replace(/\/+$/, "");
  }
  return comparable.toString();
}

/**
 * Normalize a discovered link while preserving its fragment.
 * @param {URL} url URL to normalize.
 * @returns {string} Comparable discovered link.
 */
function comparableDiscoveredUrl(url) {
  const comparable = new URL(url.toString());
  if (comparable.pathname.length > 1) {
    comparable.pathname = comparable.pathname.replace(/\/+$/, "");
  }
  return comparable.toString();
}

/**
 * Normalize visible text for compact model context.
 * @param {string} value Text to normalize.
 * @returns {string} Normalized text.
 */
function normalizeText(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/\s*\n\s*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Determine whether a target URL matches an explicit allowlist rule.
 * A trailing "*" makes the rule a URL prefix; all other rules are exact.
 * @param {URL} target Target URL.
 * @param {string} rule Allowlist rule.
 * @param {string} currentUrl Current page URL for relative rules.
 * @returns {boolean} Whether the rule allows the target.
 */
function matchesAllowedRule(target, rule, currentUrl) {
  if (typeof rule !== "string" || !rule.trim()) {
    return false;
  }

  const trimmedRule = rule.trim();
  const isPrefix = trimmedRule.endsWith("*");
  const ruleValue = isPrefix ? trimmedRule.slice(0, -1) : trimmedRule;
  const parsedRule = parseHttpUrl(ruleValue, currentUrl);
  if (!parsedRule) {
    return false;
  }

  if (isPrefix) {
    const targetWithoutHash = new URL(target.toString());
    const prefixWithoutHash = new URL(parsedRule.toString());
    targetWithoutHash.hash = "";
    prefixWithoutHash.hash = "";
    return targetWithoutHash.toString().startsWith(prefixWithoutHash.toString());
  }

  return comparableUrl(target) === comparableUrl(parsedRule);
}

/**
 * Check a URL against the configured navigation policy.
 * With no explicit rules, navigation is restricted to the current origin.
 * @param {URL} target Target URL.
 * @param {string} currentUrl Current page URL.
 * @param {string[]} allowedUrls Explicit exact or trailing-wildcard rules.
 * @param {boolean} allowlistEnabled Whether an empty explicit list denies all.
 * @returns {boolean} Whether policy allows the URL.
 */
export function isUrlAllowedByPolicy(
  target,
  currentUrl,
  allowedUrls = [],
  allowlistEnabled = false,
) {
  const current = parseHttpUrl(currentUrl, currentUrl);
  if (!current) {
    return false;
  }

  const rules = Array.isArray(allowedUrls)
    ? allowedUrls.filter((rule) => typeof rule === "string" && rule.trim())
    : [];

  if (rules.length === 0) {
    return !allowlistEnabled && target.origin === current.origin;
  }

  return rules.some((rule) => matchesAllowedRule(target, rule, currentUrl));
}

/**
 * Collect sanitized page text and links for the Page client tool.
 * Elements marked data-private and executable/non-content elements are omitted.
 * @param {Object} options Capture options.
 * @param {Document} options.document Page document.
 * @param {string} options.currentUrl Current browser URL.
 * @param {number} [options.maxTextChars] Maximum returned text length.
 * @param {number} [options.maxLinks] Maximum returned links.
 * @param {number} [options.maxLinkChars] Maximum combined link text/URL length.
 * @returns {Object} JSON-serializable page context.
 */
export function capturePageContext({
  document: pageDocument,
  currentUrl,
  maxTextChars = DEFAULT_MAX_TEXT_CHARS,
  maxLinks = DEFAULT_MAX_LINKS,
  maxLinkChars = DEFAULT_MAX_LINK_CHARS,
}) {
  if (!pageDocument?.documentElement) {
    return {
      success: false,
      error: "The current page is not available.",
    };
  }
  const currentPageUrl = parseHttpUrl(currentUrl, currentUrl);
  if (!currentPageUrl) {
    return {
      success: false,
      error: "The current page URL is unavailable.",
    };
  }
  const normalizedCurrentUrl = currentPageUrl.toString();

  const contentRoot =
    pageDocument.querySelector("main") ||
    pageDocument.body ||
    pageDocument.documentElement;
  const clone = contentRoot.cloneNode(true);
  if (clone.matches?.(EXCLUDED_CONTENT_SELECTOR)) {
    clone.textContent = "";
  } else {
    clone.querySelectorAll(EXCLUDED_CONTENT_SELECTOR).forEach((element) => {
      element.remove();
    });
  }

  const fullText = normalizeText(clone.textContent || "");
  const text = fullText.slice(0, Math.max(0, maxTextChars));
  const links = [];
  const seenLinks = new Set();
  let linkChars = 0;

  for (const anchor of pageDocument.querySelectorAll("a[href]")) {
    if (
      anchor.closest(PRIVATE_SELECTOR) ||
      anchor.closest(HIDDEN_SELECTOR) ||
      anchor.closest("ctm-chat")
    ) {
      continue;
    }

    const parsedUrl = parseHttpUrl(
      anchor.getAttribute("href"),
      normalizedCurrentUrl,
    );
    if (!parsedUrl) {
      continue;
    }

    const comparable = comparableDiscoveredUrl(parsedUrl);
    if (seenLinks.has(comparable)) {
      continue;
    }

    seenLinks.add(comparable);
    const link = {
      text: normalizeText(
        anchor.textContent ||
          anchor.getAttribute("aria-label") ||
          anchor.getAttribute("title") ||
          parsedUrl.pathname,
      ).slice(0, 200),
      url: parsedUrl.toString(),
    };
    const nextLinkChars = link.text.length + link.url.length;
    if (linkChars + nextLinkChars > maxLinkChars) {
      break;
    }
    links.push(link);
    linkChars += nextLinkChars;

    if (links.length >= maxLinks) {
      break;
    }
  }

  return {
    success: true,
    url: normalizedCurrentUrl,
    title: normalizeText(pageDocument.title).slice(0, 500),
    text,
    textTruncated: fullText.length > text.length,
    links,
  };
}

/**
 * Keep only links that the current navigation policy can open.
 * @param {Object[]} links Captured page links.
 * @param {string} currentUrl Current browser URL.
 * @param {string[]} allowedUrls Explicit policy rules.
 * @param {boolean} allowlistEnabled Whether an empty explicit list denies all.
 * @returns {Object[]} Allowed links.
 */
export function filterLinksByNavigationPolicy(
  links,
  currentUrl,
  allowedUrls = [],
  allowlistEnabled = false,
) {
  if (!Array.isArray(links)) {
    return [];
  }

  return links.filter((link) => {
    const target = parseHttpUrl(link?.url, currentUrl);
    return (
      target &&
      isUrlAllowedByPolicy(
        target,
        currentUrl,
        allowedUrls,
        allowlistEnabled,
      )
    );
  });
}

/**
 * Validate a model-requested navigation target.
 * The target must be a link on the current page and pass the URL policy.
 * @param {Object} options Validation options.
 * @param {string} options.requestedUrl URL requested by the model.
 * @param {string} options.currentUrl Current browser URL.
 * @param {Object[]} options.links Sanitized links on the current page.
 * @param {string[]} [options.allowedUrls] Explicit policy rules.
 * @param {boolean} [options.allowlistEnabled] Whether an empty list denies all.
 * @returns {Object} Success result with normalized URL, or a safe error.
 */
export function validateNavigationTarget({
  requestedUrl,
  currentUrl,
  links,
  allowedUrls = [],
  allowlistEnabled = false,
}) {
  const target = parseHttpUrl(requestedUrl, currentUrl);
  if (!target) {
    return {
      success: false,
      error: "Navigation requires a valid HTTP or HTTPS URL.",
    };
  }

  const targetComparable = comparableDiscoveredUrl(target);
  const isCurrentPageLink = Array.isArray(links) && links.some((link) => {
    const linkUrl = parseHttpUrl(link?.url, currentUrl);
    return (
      linkUrl &&
      comparableDiscoveredUrl(linkUrl) === targetComparable
    );
  });

  if (!isCurrentPageLink) {
    return {
      success: false,
      error: "The requested URL is not a link on the current page.",
    };
  }

  if (
    !isUrlAllowedByPolicy(
      target,
      currentUrl,
      allowedUrls,
      allowlistEnabled,
    )
  ) {
    return {
      success: false,
      error: "The requested URL is outside the configured navigation policy.",
    };
  }

  return {
    success: true,
    url: target.toString(),
    message: "Navigation accepted.",
  };
}
